<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { Search, X } from 'lucide-vue-next';

import useWebsiteTexts from '@/composables/useWebsiteTexts';
import { getContext } from '@/composables/useSearch';

const { websiteText } = useWebsiteTexts();
const { search } = getContext();

const searchQuery = ref('');
const searchInput = ref<HTMLInputElement | null>(null);

function clear() {
  searchQuery.value = '';
  searchInput.value?.focus();
}

onMounted(() => {
  searchInput.value?.focus();

  watch(
    () => searchQuery.value,
    () => {
      search(searchQuery.value);
    },
  );
});
</script>

<template>
  <div class="search-bar__row flex items-center gap-2">
    <input
      ref="searchInput"
      v-model="searchQuery"
      type="text"
      :placeholder="websiteText('header_searchplaceholder').value"
      class="search-bar__input min-w-0 flex-1 text-foreground placeholder:text-muted-foreground"
    />
    <button
      v-if="searchQuery"
      type="button"
      class="btn-bare shrink-0 text-muted-foreground transition-colors hover:text-foreground"
      aria-label="Rensa"
      @click="clear()"
    >
      <X class="block h-4 w-4" />
    </button>
    <Search class="pointer-events-none block h-5 w-5 shrink-0 text-muted-foreground" />
  </div>
</template>

<style scoped>
/* Underline lives on the flex row so it spans the full width under input + icons. */
.search-bar__row {
  border-bottom: 1px solid var(--color-gray-1440);
}
.search-bar__row:focus-within {
  border-bottom-color: var(--color-standard);
}

/* Fully own the input box — the global input[type="text"] selector in
   style/elements/input.css (preflight is off) outranks utility classes, so
   padding/height/line-height must be set here at matching specificity. */
input[type='text'].search-bar__input,
input[type='text'].search-bar__input:focus {
  height: auto;
  margin: 0;
  padding: 0.75rem 0;
  border: 0;
  border-radius: 0;
  background-color: transparent;
  font-size: 1rem;
  line-height: 1.5;
}

/* Match the muted-foreground used by the icons (overrides --color-placeholder from input.css). */
input[type='text'].search-bar__input::placeholder {
  color: var(--color-gray-650);
}
</style>
